import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import { useScreenSizeContext } from '@/components/ScreenSize.context';
import {
  formatVideoPresentationData,
  isEspotHtml,
  isEspotImageInfoCardList,
  isEspotInfoCardList,
  isEspotListSample,
  isEspotVideo,
  isEspotDisplayed,
  isEspotDuoList
} from '@/utils/homepageUtils';

import DuoCardList from '@/components/common/InfoCardList/DuoCardList';
import ImageCardList from '@/components/common/ImageCardList';
const ImageInfoCardList = dynamic(() => import('@/components/common/ImageInfoCardList'));
ImageInfoCardList.displayName = 'DynamicLayout';
import EspotList from '@/components/common/InfoCardList/EspotList';
import VideoPresentation from '@/components/common/VideoPresentation';
import HtmlESpot from '@/components/layout/espots/HtmlESpot';

import styles from './DynamicLayout.module.scss';
import { VirtualAssistantButton } from '@/components/virtual-assistant/virtual-assistant-button';
import { useAppDispatch, useAppSelector } from '@/redux';
import { startAssistantConversation } from '@/redux/slices/assistant.slice';
import { useTranslation } from 'next-i18next';
import { isFeatureEnabled } from '@/utils/millesimaUtils';

export const DynamicLayout = ({ componentList, isMobileDevice, withPadding = true, lazy = false, withAssistant = true }) => {
  const { isMobile, mappedBreakpoint: breakpoint } = useScreenSizeContext();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const storeConf = useAppSelector(({ root }) => root.storeConf);

  const finalList = Array.isArray(componentList) ? [...componentList] : [];

  if (isFeatureEnabled(storeConf, 'chatbot') && withAssistant) {
    finalList.splice(1, 0, { id: 'assistant' });
  }

  const isFullWidth = (espot) => isEspotVideo(espot) || isEspotHtml(espot);

  return finalList?.length > 0 ? finalList.map((cmpt) => {
    const espot = cmpt?.espot;
    const computedClass = [
      isFullWidth(espot) ? styles['full-width'] : styles['max-width'],
      withPadding && styles[`padding-${breakpoint}`],
      isEspotImageInfoCardList(espot) && styles['espot-card-list']
    ].filter(Boolean).join(' ');

    if (cmpt.id === 'assistant') {
      return (
        <div className={computedClass} key={cmpt.id}>
          <div className={styles.assistant}>
            <p className={styles.title}>{t('assistant.homePage.title')}</p>
            <p className={styles['sub-title']}>
              {t('assistant.homePage.subTitle')}
            </p>
            <VirtualAssistantButton
              size="md" onClick={() => dispatch(startAssistantConversation())} label={t('assistant.name')}
            />
          </div>
        </div>
      );
    }

    return isEspotDisplayed(espot, isMobileDevice) ? (
      <div className={computedClass} key={espot._id}>
        {isEspotHtml(espot) && (
          <HtmlESpot data={espot} />
        )}
        {isEspotImageInfoCardList(espot) && (
          <ImageInfoCardList data={espot} breakpoint={breakpoint} lazy={lazy} />
        )}
        {isEspotInfoCardList(espot) && (
          <EspotList data={espot} />
        )}
        {isEspotDuoList(espot) && !isMobile && (
          <DuoCardList data={espot} />
        )}
        {isEspotListSample(espot) && (
          <ImageCardList data={espot} breakpoint={breakpoint} />
        )}
        {isEspotVideo(espot) && (
          <VideoPresentation data={formatVideoPresentationData(espot)} breakpoint={breakpoint} />
        )}
      </div>
    ) : null;
  }) : null;
};

DynamicLayout.propTypes = {
  componentList: PropTypes.array,
  isMobileDevice: PropTypes.bool,
  withPadding: PropTypes.bool,
  lazy: PropTypes.bool
};

export default DynamicLayout;
